import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import useProductAttributes from 'data-hooks/useProductAttributes';
import usePDPPhotos from 'data-hooks/usePDPPhotos';
import { getPriceDetailsUtil } from 'data-hooks/utils';
import useModularPDPCart from 'data-hooks/useModularPDPCart';
import { useLocation } from 'react-router-dom';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import usePDPPrice from 'data-hooks/usePDPPrice';
import { getPriceValidUntil } from './utils';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';
import usePdpSelectedOptionsVariantSku from '../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPBasicDetails from '../state/context/usePDPBasicDetails';

const PDPSEOTagsInner = ({ isOutOfStock = false }) => {
  // Store the initial price (which should be the final price) so that we can
  // always output this price in the JSON LD script tag even if the price
  //  changes on the client side after the initial render (e.g. if the client
  // side app crashes or the user's location impacts the price.
  const initialPrice = useRef(null);

  const { data: attributesData } = useProductAttributes();
  const { data: priceData = {}, loading: priceLoading } = usePDPPrice();
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const { data: modularCartData } = useModularPDPCart();
  const { data: photosData } = usePDPPhotos();
  const { data: basicDetails = {} } = usePDPBasicDetails();

  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();

  const location = useLocation();

  const priceDetails = useMemo(() => {
    if (!priceData || priceLoading) {
      return {};
    }
    if (pdpIsModularBYOProduct) {
      return {
        salePriceWithOptionsPrice: modularCartData?.modularTotalPrice,
      };
    }
    return getPriceDetailsUtil(
      priceData?.price,
      priceData?.promotion,
      priceData?.optionPrice
    );
  }, [
    priceData,
    priceLoading,
    pdpIsModularBYOProduct,
    modularCartData?.modularTotalPrice,
  ]);

  const { meta: { reviews } = {} } = basicDetails ?? {};

  const roundedRating = reviews?.average_score
    ? Math.round(reviews.average_score * 10) / 10
    : 0;

  const numberOfReviews = reviews?.count || 0;

  const calculatedPrice = priceDetails.hasTieredDiscount
    ? priceDetails.leastTieredDiscountedPrice
    : priceDetails?.salePriceWithOptionsPrice;

  // Once calculatdPrice is truthy, store it as initialPrice. If
  // initialPrice.current is truthy, the value should not be updated.
  initialPrice.current = initialPrice.current || calculatedPrice;

  const price = initialPrice.current;

  const basicDetailsName = basicDetails?.name;
  const basicDetailsSku = basicDetails?.sku;
  const selectedPhotoSrc = photosData?.selectedPhoto?.src || '';
  const metaDescription = attributesData?.SEOAttributes?.meta_description;

  const initialJsonLD = useMemo(
    () => ({
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: basicDetailsName,
      sku: basicDetailsSku,
      image: selectedPhotoSrc,
      description: metaDescription,
      brand: 'Joybird',
    }),
    [basicDetailsName, basicDetailsSku, metaDescription, selectedPhotoSrc]
  );

  const offers = useMemo(() => {
    const updatedOffers = {
      '@type': 'Offer',
      url: attributesData?.SEOAttributes?.canonicalURL,
      priceCurrency: 'USD',
      price,
    };

    // ref: https://stitchindustries.atlassian.net/browse/CON-6035
    const priceValidUntil = getPriceValidUntil(priceData?.promotion);

    if (priceValidUntil) {
      updatedOffers.priceValidUntil = priceValidUntil;
    }

    updatedOffers.availability = isOutOfStock
      ? 'https://schema.org/OutOfStock'
      : 'https://schema.org/InStock';

    updatedOffers.itemCondition = 'https://schema.org/NewCondition';

    return updatedOffers;
  }, [
    attributesData?.SEOAttributes?.canonicalURL,
    isOutOfStock,
    price,
    priceData?.promotion,
  ]);

  const aggregateRating = useMemo(
    () =>
      basicDetails
        ? {
            '@type': 'AggregateRating',
            ratingValue: roundedRating,
            bestRating: '5',
            worstRating: '1',
            ratingCount: numberOfReviews,
            reviewCount: numberOfReviews,
          }
        : null,
    [basicDetails, numberOfReviews, roundedRating]
  );

  const customerReviews = basicDetails?.meta?.reviews?.sample_reviews;

  const review = useMemo(
    () =>
      customerReviews?.map(customerReview => ({
        '@type': 'Review',
        name: customerReview.title,
        reviewBody: customerReview.comment,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: customerReview?.rate || 0,
          bestRating: '5',
          worstRating: '1',
        },
        datePublished: customerReview.date.split('T')[0],
        author: { '@type': 'Person', name: customerReview.full_name },
      })),
    [customerReviews]
  );

  const jsonLD = useMemo(
    () => ({
      ...initialJsonLD,
      offers,
      ...(aggregateRating && { aggregateRating }),
      ...(review && { review }),
    }),
    [initialJsonLD, offers, aggregateRating, review]
  );

  const productNameFromRouterState = location?.state?.name;
  const pageTitle = basicDetails?.name || productNameFromRouterState || '';

  const jsonLDString = useMemo(() => (jsonLD ? JSON.stringify(jsonLD) : null), [
    jsonLD,
  ]);

  return (
    <Helmet>
      <title>{pageTitle ? `${pageTitle} | Joybird` : `Joybird`}</title>

      <meta
        name="description"
        content={attributesData?.SEOAttributes?.meta_description || ''}
      />

      <meta
        name="keywords"
        content={attributesData?.SEOAttributes?.meta_keywords || ''}
      />

      <meta property="og:type" content="product" />
      <meta property="og:title" content={pageTitle} />

      <meta
        property="og:description"
        content={attributesData?.SEOAttributes?.meta_description || ''}
      />

      <meta property="og:price:amount" content={price || ''} />
      <meta property="og:price:currency" content="USD" />

      <meta
        property="og:availability"
        content={isOutOfStock ? 'Out of Stock' : 'Ready to Ship'}
      />

      {attributesData?.SEOAttributes?.canonicalURL && (
        <>
          <meta
            property="og:url"
            content={attributesData?.SEOAttributes?.canonicalURL}
          />

          <link
            id="canonical"
            href={attributesData?.SEOAttributes?.canonicalURL}
            rel="canonical"
          />
        </>
      )}

      {photosData?.selectedPhoto?.src && (
        <meta property="og:image" content={photosData?.selectedPhoto?.src} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${pageTitle} | Joybird`} />

      <meta
        name="twitter:description"
        content={attributesData?.SEOAttributes?.meta_description?.value || ''}
      />

      <meta
        data-rh="true"
        name="sku"
        content={`${basicDetails?.sku}${selectedOptionsVariantSku}`}
      />

      {photosData?.selectedPhoto?.src && (
        <meta name="twitter:image" content={photosData.selectedPhoto.src} />
      )}

      {jsonLDString && (
        <script type="application/ld+json">{jsonLDString}</script>
      )}
    </Helmet>
  );
};

PDPSEOTagsInner.propTypes = {
  isOutOfStock: PropTypes.bool,
};

const PDPSEOTags = props => (
  <SuspenseWithLoader>
    <PDPSEOTagsInner {...props} />
  </SuspenseWithLoader>
);

export default memo(PDPSEOTags);
